import { FIELD_LOG_FOOTAGE_LOOKUP_MAX_FILENAMES } from '@/features/field-log/config/constants';
import type { FieldLogFootageSelectionCandidate } from '@/features/field-log/lib/footageCandidates';
import type {
  FieldLogFootageLookupRequest,
  FieldLogFootageLookupResponse,
  FieldLogFootageLookupStatus,
} from '@/features/field-log/types/fieldLog';
import { z } from 'zod';

/** Frontend-owned lookup form values; the API request has its own contract below. */
export const fieldLogFootageLookupFormSchema = z.object({ filenamesText: z.string() }).strict();

/** Closed request body sent to the exact filename lookup endpoint. */
export const fieldLogFootageLookupRequestSchema = z
  .object({
    filenames: z.array(z.string().min(1)).min(1).max(FIELD_LOG_FOOTAGE_LOOKUP_MAX_FILENAMES),
    organizationId: z.string().min(1).optional(),
    projectId: z.string().min(1).optional(),
    sessionId: z.string().min(1).optional(),
    toleranceMinutes: z.number().int().min(0).optional(),
  })
  .strict();

/** Splits a pasted filename list without changing exact filename spelling or duplicate entries. */
export function fieldLogFootageLookupFilenames(filenamesText: string): string[] {
  return filenamesText
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((filename) => filename.length > 0);
}

/** Builds the endpoint body from the dialog's current scope and submitted text. */
export function fieldLogFootageLookupRequest(
  filenamesText: string,
  scope: Pick<FieldLogFootageLookupRequest, 'organizationId' | 'projectId' | 'sessionId'>,
): FieldLogFootageLookupRequest {
  return fieldLogFootageLookupRequestSchema.parse({
    filenames: fieldLogFootageLookupFilenames(filenamesText),
    ...scope,
  }) as FieldLogFootageLookupRequest;
}

/** Compares submitted lookup bodies so an unchanged search can explicitly refresh its query. */
export function fieldLogFootageLookupRequestEqual(
  left: FieldLogFootageLookupRequest,
  right: FieldLogFootageLookupRequest,
): boolean {
  return (
    left.organizationId === right.organizationId &&
    left.projectId === right.projectId &&
    left.sessionId === right.sessionId &&
    left.toleranceMinutes === right.toleranceMinutes &&
    left.filenames.length === right.filenames.length &&
    left.filenames.every((filename, index) => filename === right.filenames[index])
  );
}

export interface FieldLogFootageLookupViewResult {
  inputIndex: number;
  filename: string;
  status: FieldLogFootageLookupStatus;
  matches: FieldLogFootageSelectionCandidate[];
}

/** UI projection of the raw filename-lookup response. */
export interface FieldLogFootageLookupView {
  results: FieldLogFootageLookupViewResult[];
}

/** Maps raw lookup DTOs to the common candidate-row model while preserving result order. */
export function fieldLogFootageLookupView(
  response: FieldLogFootageLookupResponse,
): FieldLogFootageLookupView {
  return {
    results: response.results.map((result) => ({
      inputIndex: result.inputIndex,
      filename: result.filename,
      status: result.status,
      matches: result.matches.map((match) => ({
        assetId: match.assetId,
        filename: match.filename,
        organizationId: match.organizationId,
        projectId: match.projectId,
        recordingStartUtc: match.recordingStartUtc,
        recordingEndUtc: match.recordingEndUtc,
        serialMatch: match.comparison?.serialMatch ?? null,
        withinTimeWindow: match.comparison?.withinTimeWindow ?? null,
        alreadyLinked: match.linkedToSessionId !== null,
        linkedToSessionId: match.linkedToSessionId,
      })),
    })),
  };
}
